import { Link, useLocation, useNavigate } from "react-router-dom";
import {
	Search,
	Shuffle,
	Upload,
	User,
	Menu,
	X,
	TrendingUp,
	Grid3X3,
	Star,
	Library,
	LayoutDashboard,
	Zap,
	Rocket,
	Puzzle,
	Swords,
	Gamepad2,
	ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useAuthOverlay } from "../overlay/AuthOverlay";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SeoHead from "./SeoHead";
import { cn, logger } from "../lib/utils";
import { apiGet } from "../lib/axiosInstance";

const ResponsiveSearchBar = lazy(() => import("./ResponsiveSearchBar"));
const categoryItems = [
	{
		title: "Action",
		icon: Zap,
		search: "action",
	},
	{
		title: "Adventure",
		icon: Rocket,
		search: "adventure",
	},
	{
		title: "Puzzle",
		icon: Puzzle,
		search: "puzzle",
	},
	{
		title: "Fighting",
		icon: Swords,
		search: "fighting",
	},
	{
		title: "Arcade",
		icon: Gamepad2,
		search: "arcade",
	},
];
const navItems = [
	{
		title: "Trending",
		to: {
			pathname: "/",
			search: "?sort=trending",
		},
		icon: TrendingUp,
	},
	{ title: "Tester Hub", to: "/tester-hub", icon: Shuffle },
	{
		title: "Categories",
		to: {
			pathname: "/",
			search: "?showFilters=1",
		},
		icon: Grid3X3,
	},
	{
		title: "Top Rated",
		to: {
			pathname: "/",
			search: "?sort=top-rated",
		},
		icon: Star,
	},
	{ title: "Devlog Reels", to: "/devlog", icon: Library },
	{ title: "Leaderboards", to: "/leaderboards", icon: LayoutDashboard },
];
function useDebounce(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(handler);
	}, [value, delay]);

	return debounced;
}

const TopNav = ({ onMenuToggle }) => {
	const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const { user, logout } = useAuth();
	const { open } = useAuthOverlay();
	const navigate = useNavigate();
	const location = useLocation();


	const [searchDraft, setSearchDraft] = useState("");
	const debouncedSearch = useDebounce(searchDraft, 300);

	const [showMobileSearch, setShowMobileSearch] = useState(false);
	const [searchSuggestions, setSearchSuggestions] = useState([]);
	const [searchSuggestionsLoading, setSearchSuggestionsLoading] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const currentSearch = useMemo(() => searchParams.get("search") || "", [searchParams]);

	useEffect(() => {
		if (!showMobileSearch) {
			setSearchDraft(currentSearch);
		}
	}, [currentSearch, showMobileSearch]);

	useEffect(() => {
		if (!showMobileSearch) return;
		const onKeyDown = (e) => {
			if (e.key === "ArrowDown") {
				setActiveIndex((prev) => Math.min(prev + 1, searchSuggestions.length - 1));
			}
			if (e.key === "ArrowUp") {
				setActiveIndex((prev) => Math.max(prev - 1, 0));
			}
			if (e.key === "Enter" && activeIndex >= 0) {
				const game = searchSuggestions[activeIndex];
				navigate(`/game/${game.slug}`);
				setShowMobileSearch(false);
			}
			if (e.key === "Escape") setShowMobileSearch(false);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [showMobileSearch]);

	function openSearchOverlay() {
		setShowMobileSearch(true);
	}

	useEffect(() => {
		if (!showMobileSearch) return;

		const query = String(searchDraft || "").trim();
		if (query.length < 2) {
			setSearchSuggestions([]);
			setSearchSuggestionsLoading(false);
			return;
		}

		let cancelled = false;
		setSearchSuggestionsLoading(true);
	
		const handle = window.setTimeout(async () => {
			try {
				const response = await apiGet(
					`/games?search=${encodeURIComponent(query)}&limit=8&page=1`
				);
				const next = Array.isArray(response?.games) ? response.games : [];
				if (!cancelled) setSearchSuggestions(next);
			} catch (_error) {
				if (!cancelled) setSearchSuggestions([]);
			} finally {
				if (!cancelled) setSearchSuggestionsLoading(false);
			}
		}, 250);

		return () => {
			cancelled = true;
			window.clearTimeout(handle);
		};
	}, [searchDraft, showMobileSearch]);

	function isNavItemActive(item) {
		if (typeof item.to === "string") return location.pathname === item.to;
		if (location.pathname !== item.to.pathname) return false;

		const required = new URLSearchParams(item.to.search || "");
		for (const [key, value] of required.entries()) {
			if (searchParams.get(key) !== value) return false;
		}
		return true;
	}

	function handleAvatarClick() {
		if (!user) return open("login");
		setIsMenuOpen((prev) => !prev);
	}

	function handleLogout() {
		if (!window.confirm("Log out?")) return;
		logout();
		setIsMenuOpen(false);
		navigate("/");
	}
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (showMobileSearch) {
			requestAnimationFrame(() => setMounted(true));
		} else {
			setMounted(false);
		}
	}, [showMobileSearch]);
	
	const categoryGroups = [
		{
			title: "Discover",
			items: [
				{
					title: "Trending",
					to: {
						pathname: "/",
						search: "?sort=trending",
					},
					icon: TrendingUp,
				},
				{
					title: "Top Rated",
					to: {
						pathname: "/",
						search: "?sort=top-rated",
					},
					icon: Star,
				},
				{
					title: "Random",
					to: {
						pathname: "/",
						search: "?random=true",
					},
					icon: Shuffle,
				},
			],
		},

		{
			title: "Community",
			items: [
				{
					title: "Tester Hub",
					to: "/tester-hub",
					icon: Shuffle,
				},
				{
					title: "Devlog Reels",
					to: "/devlog",
					icon: Library,
				},
				{
					title: "Leaderboards",
					to: "/leaderboards",
					icon: LayoutDashboard,
				},
			],
		},

		{
			title: "Genres",
			items: [
				{
					title: "Action",
					to: "/?genre=action",
					icon: Zap,
				},
				{
					title: "Puzzle",
					to: "/?genre=puzzle",
					icon: Puzzle,
				},
				{
					title: "Adventure",
					to: "/?genre=adventure",
					icon: Rocket,
				},
				{
					title: "Fighting",
					to: "/?genre=fighting",
					icon: Swords,
				},
			],
		},
	];

	const renderedNavItems = useMemo(() => {
		return categoryGroups.map((group) => (
			<div
				key={group.title}
				className="relative group"
			>
				<button
					className={cn(
						"inline-flex items-center gap-2 rounded-xl px-3 py-2",
						"text-xs xl:text-sm font-medium transition-colors",
						"text-muted-foreground hover:text-foreground hover:bg-accent/15"
					)}
				>
					<span>{group.title}</span>

					<ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
				</button>

				<div
					className={cn(
						"absolute left-0 top-full mt-2 w-72",
						"rounded-2xl border border-border",
						"bg-popover/95 backdrop-blur-xl shadow-2xl",
						"opacity-0 invisible translate-y-2",
						"transition-all duration-200",
						"group-hover:opacity-100",
						"group-hover:visible",
						"group-hover:translate-y-0"
					)}
				>
					<div className="p-2">
						{group.items.map((item) => {
							const to =
								typeof item.to === "string"
									? item.to
									: `${item.to.pathname}${item.to.search || ""}`;

							const Icon = item.icon;
							const active = isNavItemActive(item);

							return (
								<Link
									key={item.title}
									to={to}
									className={cn(
										"flex items-center gap-3 rounded-xl p-3 transition-colors",
										active
											? "bg-accent/25 text-foreground"
											: "text-muted-foreground hover:text-foreground hover:bg-accent/15"
									)}
								>
									<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15">
										<Icon className="h-4 w-4" />
									</div>

									<div className="min-w-0">
										<p className="text-sm font-medium truncate">
											{item.title}
										</p>

										<p className="text-xs text-muted-foreground">
											Explore {item.title}
										</p>
									</div>
								</Link>
							);
						})}
					</div>
				</div>
			</div>
		));
	}, [location.pathname, location.search]);
	return (
		<header className={cn(
			"fixed inset-x-0 top-0 z-50 border-b border-border",
			"bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70"
		)}>
			<SeoHead path={location.pathname} />

			<div className="mx-auto flex h-14 sm:h-16 items-center justify-between px-2 sm:px-4 lg:px-6 gap-2 md:flex-nowrap">
			<div className="flex items-center gap-2 sm:gap-3 min-w-0">
				<Button
					variant="ghost"
					size="icon"
					onClick={onMenuToggle}
					className="h-10 w-10 rounded-xl"
					aria-label="Open menu"
				>
					<Menu className="h-5 w-5" />
				</Button>

				<Link to="/" 
					className="flex items-center gap-3 group min-w-0"
					title="Coffee Game - Home"
					aria-label="Coffee Game - Home"	
				>
					<img
						src="/android-chrome-512x512.png"
						alt="Coffee Game Online Logo"
						loading="lazy"
						className="h-9 w-9 sm:h-10 sm:w-10 border border-border/50 rounded-full object-cover shadow-sm transition-transform group-hover:scale-105"
					/>

					<div className="hidden sm:flex flex-col leading-none select-none">
						<span
							className={cn(
								"text-base md:text-xl font-bold tracking-tight",
								"text-foreground transition-colors",
								"group-hover:text-primary"
							)}
						>
							Coffee Game
						</span>

						<div className="mt-1 flex items-center gap-2">
							<div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />

							<span
								className={cn(
									"text-[10px] md:text-xs font-semibold uppercase tracking-[0.18em]",
									"text-primary/90"
								)}
							>
								Online
							</span>
						</div>
					</div>
				</Link>
			</div>

			<nav className="hidden lg:flex items-center gap-2 mx-4">
				{renderedNavItems}
			</nav>
			<div className="hidden lg:flex flex-1 max-w-xl ml-2 lg:ml-4">
				<button
					type="button"
					onClick={openSearchOverlay}
					className={cn(
						"w-full h-10 rounded-2xl border border-border",
						"bg-secondary/40 hover:bg-secondary transition",
						"flex items-center gap-2 px-3 text-sm",
						"focus:outline-none focus:ring-2 focus:ring-primary/60"
					)}
					aria-label="Open search"
				>
					<Search className="h-5 w-5 text-foreground" />
					<span className={cn(
						"min-w-0 flex-1 truncate text-left",
						currentSearch ? "text-foreground" : "text-muted-foreground"
					)}>
						{currentSearch || "Search games..."}
					</span>
				</button>
			</div>

			<div className="flex items-center gap-1 sm:gap-2">

				<Button
					variant="ghost"
					size="icon"
					className="md:hidden"
					onClick={openSearchOverlay}
					aria-label="Search"
				>
					<Search className="h-5 w-5" />
				</Button>

				<Link className="md:hidden" to="/?random=true">
					<Button variant="ghost" size="sm" className="gap-2 rounded-xl">
						<Shuffle className="h-4 w-4" />
						<span className="hidden sm:inline">Random</span>
					</Button>
				</Link>

				<Link 
					to="/upload"
					className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent/15"
				>
					<Button size="sm" className="rounded-xl">
						<Upload className="h-4 w-4" />
						<span className="hidden md:inline text-primary-foreground">
							Upload
						</span>
					</Button>
				</Link>

				<div className="relative">
					<button
						onClick={handleAvatarClick}
						className={cn(
							"h-10 w-10 rounded-full overflow-hidden border border-border",
							"hover:ring-2 hover:ring-primary/60 transition",
							"focus:outline-none focus:ring-2 focus:ring-primary/60 focus:ring-offset-2"
						)}
						aria-haspopup="menu"
						aria-expanded={user ? isMenuOpen : undefined}
					>
						{user ? (
							<img
								src={user.profileImage || `https://api.dicebear.com/9.x/pixel-art/svg?seed=${encodeURIComponent(user.username)}`}
								alt="avatar"
								loading="lazy"
								decoding="async"
								className="h-full w-full object-cover"
							/>
						) : (
							<div className="flex items-center justify-center h-full bg-secondary">
								<User className="h-4 w-4 text-secondary-foreground" />
							</div>
						)}
					</button>

					{user && isMenuOpen && (
						<div className="absolute right-0 mt-3 w-44 bg-popover border border-border rounded-xl shadow-card overflow-hidden">

							<button
								onClick={() => {
									setIsMenuOpen(false);
									navigate("/profile");
								}}
								className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-accent/20 transition-colors"
							>
								Profile
							</button>

							<button
								onClick={handleLogout}
								className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-accent/20 hover:text-primary transition-colors"
							>
								Logout
							</button>
						</div>
					)}
				</div>
			</div>
		</div>

		{showMobileSearch && mounted &&
			createPortal(
				<div className="fixed inset-0 z-[9999] bg-background/60 backdrop-blur-2xl">
					<div className="mx-auto w-full max-w-5xl px-4 sm:px-8 pt-4 sm:pt-8">
						<div className="flex items-center gap-2">
							<div className="flex-1">
								<Suspense
									fallback={<div className="h-10 w-full rounded-2xl border border-border bg-accent/15" />}
								>
									<ResponsiveSearchBar
										autoFocus
										fullWidth
										value={searchDraft}
										onValueChange={setSearchDraft}
										onSubmitted={() => setShowMobileSearch(false)}
										className="h-12"
									/>
								</Suspense>
							</div>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => setShowMobileSearch(false)}
								className="h-10 w-10 rounded-xl"
								aria-label="Close search"
							>
								<X className="h-5 w-5" />
							</Button>
						</div>

						<div className="mt-6">
							<div className="px-4 py-3 border-b border-border flex items-center justify-between">
								<p className="text-sm font-medium text-foreground">Results</p>
								{searchSuggestionsLoading ? (
									<p className="text-xs text-muted-foreground">Searching…</p>
								) : (
									<p className="text-xs text-muted-foreground">Type 2+ letters</p>
								)}
							</div>

							<div className="max-h-[72vh] overflow-y-auto">
								{!String(searchDraft || "").trim() ? (
									<div className="p-4 text-sm text-muted-foreground">
										Start typing to search games.
									</div>
								) : searchSuggestionsLoading ? (
									<div className="p-4 text-sm text-muted-foreground">Loading…</div>
								) : searchSuggestions.length ? (
									<div className="divide-y divide-border">
										{searchSuggestions.map((game) => (
											<button
												type="button"
												key={game._id || game.id || game.slug}
												onClick={() => {
													setShowMobileSearch(false);
													navigate(`/game/${game.slug}`);
												}}
												className="w-full text-left p-4 sm:p-5 hover:bg-accent/15 transition flex items-center gap-4"
											>
												<img
													src={game.thumbnail || "https://placehold.co/300x200"}
													alt={game.title}
													className="h-14 w-20 rounded-xl object-cover border border-border"
												/>
												<div className="min-w-0 flex-1">
													<p className="text-base font-medium text-foreground truncate">{game.title}</p>
													<p className="text-xs text-muted-foreground truncate">{game.genre || "Game"}</p>
												</div>
											</button>
										))}
									</div>
								) : (
									<div className="p-4 text-sm text-muted-foreground">No games found.</div>
								)}
							</div>

							<div className="px-4 py-3 border-t border-border flex items-center justify-between gap-3">
								<Button
									variant="ghost"
									onClick={() => {
										setSearchDraft("");
										setSearchSuggestions([]);
									}}
								>
									Clear
								</Button>
								<Button
									onClick={() => {
										const trimmed = String(searchDraft || "").trim();
										if (!trimmed) return;
										setShowMobileSearch(false);
										navigate({ pathname: "/", search: `?search=${encodeURIComponent(trimmed)}&page=1` });
									}}
									disabled={!String(searchDraft || "").trim()}
								>
									View all
								</Button>
							</div>
						</div>
					</div>
				</div>,
				document.body
			)}
		</header>
	);
};

export default TopNav;